"use client";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Paper,
  Divider,
  Container,
  Button,
  useMediaQuery,
} from "@mui/material";
import { deepOrange, blue, grey } from "@mui/material/colors";
import { styled, useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import Methods from "../../utility/Methods";

const ProfileInfoItem = ({ icon, label, value }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
      <Box sx={{ mr: 2, color: "#0284c9" }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="medium">
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  fontSize: 48,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    width: 100,
    height: 100,
    fontSize: 40,
  },
}));

const ProfilePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get user data
  const role = Methods.getRole();
  const emailId = Methods.getEmail();

  // Function to get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case "ADMIN":
        return "Administrator";
      case "USER":
        return "Standard User";
      default:
        return role || "Guest";
    }
  };

  // Function to get avatar background color based on role
  const getAvatarColor = (role) => {
    switch (role) {
      case "ADMIN":
        return deepOrange[500];
      case "USER":
        return blue[500];
      default:
        return grey[500];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Header Banner */}
        {/* <Box
          sx={{
            height: isMobile ? 100 : 150,
            background: "linear-gradient(45deg, #0284c9 30%, #f5841f 90%)",
            position: "relative",
          }}
        /> */}

        <Grid container sx={{ mt: 12 }}>
          {/* Avatar Section */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              mt: isMobile ? -5 : -8,
              p: 4,
              borderRight: { xs: "none", md: "1px solid #e0e0e0" },
            }}
          >
            <StyledAvatar
              sx={{
                bgcolor: getAvatarColor(role),
                border: "4px solid white",
              }}
            >
              {emailId?.slice(0, 2).toUpperCase()}
            </StyledAvatar>

            <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>
              {emailId?.split("@")[0] || "User"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                backgroundColor: getAvatarColor(role),
                color: "#fff",
                px: 2,
                py: 0.5,
                borderRadius: 10,
                mt: 1,
              }}
            >
              {getRoleDisplayName(role)}
            </Typography>

            {/* <Button variant="outlined" startIcon={<EditIcon />} sx={{ mt: 3 }}>
              Edit Profile
            </Button> */}
          </Grid>

          {/* Profile Details Section */}
          <Grid item xs={12} md={8} sx={{ p: 4, mx: 2 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
              Profile Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <ProfileInfoItem
              icon={<PersonIcon />}
              label="Full Name"
              value={emailId?.split("@")[0] || "User"}
            />

            <ProfileInfoItem
              icon={<EmailIcon />}
              label="Email Address"
              value={emailId || "Not available"}
            />

            <ProfileInfoItem
              icon={<BadgeIcon />}
              label="Role"
              value={getRoleDisplayName(role)}
            />

            {/* Additional info sections could be added here */}
            {/* <Divider sx={{ my: 3 }} />

            <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
              Account Settings
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" sx={{ mr: 2 }}>
                Change Password
              </Button>
              <Button variant="outlined" color="error">
                Delete Account
              </Button>
            </Box> */}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ProfilePage;
